// src/features/WhatIfAnalysis/hooks/useWhatIfAnalysis.js
import { useState, useEffect, useCallback } from 'react';
import { WhatIfEngine } from '../WhatIfEngine';
import { getData } from '../../../services/storageService';
import { useI18n } from '../../../i18n/i18n';

export const useWhatIfAnalysis = () => {
  const [engine] = useState(() => new WhatIfEngine());
  const [scenarios, setScenarios] = useState(new Map());
  const [currentScenario, setCurrentScenario] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [baseDataLoaded, setBaseDataLoaded] = useState(false);
  const { t } = useI18n();

  // Завантаження базових даних
  const loadBaseData = useCallback(async () => {
    if (baseDataLoaded) return true;

    setIsLoading(true);
    setError(null);

    try {
      const records = await getData('lainsa_records');
      if (!Array.isArray(records) || records.length === 0) {
        throw new Error(t('common_loadError'));
      }

      await engine.loadBaseData(records);
      setBaseDataLoaded(true);
      return true;
    } catch (err) {
      console.error('What-if analysis base data loading error:', err);
      setError(err.message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [engine, baseDataLoaded, t]);

  // Отримання попередньо визначених сценаріїв
  const getPredefinedScenarios = useCallback(() => {
    return engine.getPredefinedScenarios(t);
  }, [engine, t]);

  // Створення нового сценарію
  const createScenario = useCallback(async (name, parameters) => {
    const dataLoaded = await loadBaseData();
    if (!dataLoaded) {
      throw new Error('Base data not loaded');
    }

    const scenario = engine.createScenario(name, parameters, t);
    setScenarios(new Map(engine.scenarios));
    setCurrentScenario(scenario.id);
    
    return scenario;
  }, [engine, loadBaseData, t]);

  // Запуск аналізу сценарію
  const runScenarioAnalysis = useCallback(async (scenarioId) => {
    setIsLoading(true);
    setError(null);

    try {
      const scenario = await engine.runScenarioAnalysis(scenarioId, t);
      setScenarios(new Map(engine.scenarios));
      return scenario;
    } catch (err) {
      console.error('What-if analysis error:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [engine, t]);

  // Запуск попередньо визначеного сценарію
  const runPredefinedScenario = useCallback(async (scenarioType) => {
    const predefined = getPredefinedScenarios().find(s => s.id === scenarioType);
    if (!predefined) {
      throw new Error(`Unknown scenario type: ${scenarioType}`);
    }

    const scenario = await createScenario(predefined.name, predefined.parameters);
    return await runScenarioAnalysis(scenario.id);
  }, [createScenario, runScenarioAnalysis, getPredefinedScenarios]);

  // Отримання сценарію за ID
  const getScenario = useCallback((scenarioId) => {
    return engine.scenarios.get(scenarioId);
  }, [engine]);

  // Видалення сценарію
  const deleteScenario = useCallback((scenarioId) => {
    engine.scenarios.delete(scenarioId);
    setScenarios(new Map(engine.scenarios));
    
    if (currentScenario === scenarioId) {
      setCurrentScenario(null);
    }
  }, [engine, currentScenario]);

  // Очищення всіх сценаріїв
  const clearAllScenarios = useCallback(() => {
    engine.scenarios.clear();
    setScenarios(new Map());
    setCurrentScenario(null);
  }, [engine]);

  // Автоматичне завантаження базових даних
  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  return {
    // Стан
    scenarios: Array.from(scenarios.values()),
    currentScenario: currentScenario ? getScenario(currentScenario) : null,
    isLoading,
    error,
    baseDataLoaded,
    
    // Дії
    createScenario,
    runScenarioAnalysis,
    runPredefinedScenario,
    getScenario,
    deleteScenario,
    clearAllScenarios,
    getPredefinedScenarios,
    loadBaseData,
    
    // Утиліти
    hasScenarios: scenarios.size > 0,
    completedScenarios: Array.from(scenarios.values()).filter(s => s.status === 'completed').length
  };
};

export default useWhatIfAnalysis;